import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'

const all = new Map<string, GeckoBinSize>()

/**
 * A class representing bin dimensions. Note that each possible bin size
 * (i.e. 3 inches x 3 inches) is represented by a single instance.
 */
export class GeckoBinSize {
  //----------------------------------------------------------------------------

  public static readonly Description = 'The dimensions of the data grid'

  //----------------------------------------------------------------------------

  public static readonly OneInchBins = new GeckoBinSize(
    GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Inches)
  )
  public static readonly ThreeInchBins = new GeckoBinSize(
    GeckoMeasurement.CreateFromValue(3, GeckoMeasurementUnit.Inches)
  )
  public static readonly SixInchBins = new GeckoBinSize(
    GeckoMeasurement.CreateFromValue(6, GeckoMeasurementUnit.Inches)
  )
  public static readonly TwelveInchBins = new GeckoBinSize(
    GeckoMeasurement.CreateFromValue(12, GeckoMeasurementUnit.Inches)
  )

  //----------------------------------------------------------------------------

  /** A unique ID which should line up with Data Systems and even Portal */
  public readonly id: string

  /** A bin width along the surface of a component (for example, 1 tube) */
  public readonly width: GeckoMeasurement

  /** A bin height along the surface of a component (for example, 3 inches) */
  public readonly height: GeckoMeasurement

  //----------------------------------------------------------------------------

  private constructor(width: GeckoMeasurement, height?: GeckoMeasurement) {
    this.width = width
    this.height = height ?? this.width
    this.id = `${this.width} x ${this.height}`
    all.set(this.id, this)
  }

  //----------------------------------------------------------------------------

  /**
   * A default sorting method for component data frame bin sizes
   * @param a The first bin size object
   * @param b The second bin size object
   */
  public static DefaultSort(a: GeckoBinSize, b: GeckoBinSize): number {
    if (a.width.unit !== b.width.unit)
      return a.width.unit > b.width.unit ? -1 : 1
    if (a.height !== b.height) return a.height.num - b.height.num
    if (a.height.unit !== b.height.unit)
      return a.height.unit > b.height.unit ? -1 : 1
    return a.width.num - b.width.num
  }

  /**
   * Returns the unique object for a given ID
   * @param id The unique ID of this object
   */
  public static GetByID(id: string | undefined): GeckoBinSize | undefined {
    return all.get(id ?? '')
  }

  /**
   * Returns the unique bin size instance for a given ID, or creates a unique
   * bin size instance from scratch for this new ID
   * @param width       The numeric bin width
   * @param widthUnit   The bin width measurement unit
   * @param height      The numeric bin height (defaults to width)
   * @param heightUnit  The bin height measurement unit (defaults to widthUnit)
   * @constructor
   */
  public static GetOrCreate(
    width: number,
    widthUnit: GeckoMeasurementUnit,
    height?: number,
    heightUnit?: GeckoMeasurementUnit
  ) {
    const w = GeckoMeasurement.CreateFromValue(width, widthUnit).convertTo(
      getPreferredBinDimension(widthUnit)
    )
    const h =
      !!height && !!heightUnit
        ? GeckoMeasurement.CreateFromValue(height, heightUnit).convertTo(
            getPreferredBinDimension(heightUnit)
          )
        : w
    return GeckoBinSize.GetByID(`${w} x ${h}`) ?? new GeckoBinSize(w, h)
  }
}

const getPreferredBinDimension = (
  inputUnit: GeckoMeasurementUnit
): GeckoMeasurementUnit => {
  if (inputUnit === GeckoMeasurementUnit.Feet)
    return GeckoMeasurementUnit.Inches
  if (inputUnit === GeckoMeasurementUnit.Meters)
    return GeckoMeasurementUnit.Millimeters
  else return inputUnit
}
